import React, { useState } from "react";
import { BrandLogoControl } from "./BrandLogoControl";

interface GeneratedImagesProps {
  imageUrls: string[];
  prompt?: string;
  provider?: string;
  /** When set (e.g. image generation failed), show error state instead of grid. */
  error?: string;
  /** Both required before the brand-logo control is offered (SGL-344). */
  customerId?: string;
  idToken?: string;
}

export const GeneratedImages: React.FC<GeneratedImagesProps> = ({
  imageUrls,
  prompt,
  provider,
  error,
  customerId,
  idToken,
}) => {
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  /**
   * Composited results, by the index of the image they replace (SGL-344).
   *
   * Kept beside `imageUrls` rather than written over it: the original is what
   * every re-composite starts from, so flipping the variant or moving the mark
   * does not stack a second logo on the first.
   */
  const [brandedUrls, setBrandedUrls] = useState<Record<number, string>>({});

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  };

  if (error) {
    return (
      <div className="my-4 p-4 rounded-lg bg-red-50 border border-red-200">
        <p className="text-sm text-red-700">{error}</p>
        <p className="text-xs text-red-600 mt-1">You can try again using the Regenerate action below.</p>
      </div>
    );
  }

  return (
    <div className={`my-4 ${imageUrls.length === 1 ? "max-w-[280px]" : "max-w-sm"}`}>
      {/* Info Badge */}
      {(prompt || provider) && (
        <div className="mb-3 flex items-center gap-2 text-sm text-[#2f4941]/60">
          {provider && (
            <span className="px-2 py-1 bg-[#2f4941]/10 rounded-md font-medium">
              {provider === "openai" ? "OpenAI" : provider === "gemini" ? "Gemini" : provider}
            </span>
          )}
          {imageUrls.length > 1 && (
            <span className="text-xs">{imageUrls.length} images generated</span>
          )}
        </div>
      )}

      {/* Image Grid - Dynamic layout based on number of images */}
      <div
        className={`grid gap-3 ${
          imageUrls.length === 1
            ? "grid-cols-1"
            : imageUrls.length === 2
            ? "grid-cols-2"
            : imageUrls.length === 3
            ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
            : imageUrls.length === 4
            ? "grid-cols-2 md:grid-cols-2 lg:grid-cols-4"
            : imageUrls.length <= 6
            ? "grid-cols-2 md:grid-cols-3"
            : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        }`}
      >
        {imageUrls.map((url, index) => (
          <div key={index}>
            <div
              className="relative group cursor-pointer animate-fadeIn"
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={() => setSelectedImage(brandedUrls[index] ?? url)}
            >
            {/* Loading placeholder */}
            {!loadedImages.has(index) && (
              <div className="absolute inset-0 bg-[#2f4941]/10 rounded-lg flex items-center justify-center">
                <div className="w-8 h-8 border-3 border-[#2f4941] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Image */}
            <img
              src={brandedUrls[index] ?? url}
              alt={`Generated image ${index + 1}${
                prompt ? ` - ${prompt.substring(0, 50)}...` : ""
              }`}
              className={`w-full h-auto rounded-lg border-2 border-[#2f4941]/20 transition-all ${
                loadedImages.has(index)
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none"
              } group-hover:border-[#2f4941] group-hover:shadow-lg`}
              onLoad={() => handleImageLoad(index)}
              loading="lazy"
            />

            {/* Expand icon on hover */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-black/70 p-2 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                  />
                </svg>
                </div>
              </div>
            </div>

            {customerId && idToken && (
              <BrandLogoControl
                imageUrl={url}
                customerId={customerId}
                idToken={idToken}
                onApplied={(result) =>
                  setBrandedUrls((prev) => ({ ...prev, [index]: result.image_url }))
                }
              />
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <img
            src={selectedImage}
            alt="Full size preview"
            className="max-w-[75vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
